import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Archive, ChevronRight, Download, DownloadCloud, FileVideo, Folder, HardDrive, Home,
  Loader2, RefreshCw, ShieldAlert,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

function fmtSize(b) {
  if (b == null) return "—";
  const n = Number(b);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(mtime) {
  if (!mtime) return "—";
  try { return new Date(Number(mtime) * 1000).toLocaleString("ro-RO"); } catch { return "—"; }
}

export default function NasArchivePage() {
  const { isAdmin } = useAuth();
  const [path, setPath] = useState(null); // null = use basePath from status
  const [projectId, setProjectId] = useState("");
  const [cameraId, setCameraId] = useState("");

  const { data: status } = useQuery({ queryKey: ["nas-status"], queryFn: api.nas.status, enabled: isAdmin });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: isAdmin });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list(), enabled: isAdmin });

  const effPath = path ?? status?.basePath ?? "";
  const { data: listing, isFetching, refetch, error } = useQuery({
    queryKey: ["nas-browse", effPath],
    queryFn: () => api.nas.browse(effPath || undefined),
    enabled: isAdmin && !!status?.configured,
    retry: false,
  });

  const camerasForProject = useMemo(
    () => (cameras || []).filter((c) => !projectId || String(c.projectId) === String(projectId)),
    [cameras, projectId]
  );

  const [busy, setBusy] = useState("");
  const downloadFile = async (item) => {
    setBusy(item.path);
    try {
      const blob = await api.nas.downloadBlob(item.path);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = item.name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descărcat: ${item.name}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const importFile = useMutation({
    mutationFn: (item) => api.nas.import({ path: item.path, cameraId: Number(cameraId), projectId: Number(projectId) }),
    onSuccess: (r) => toast[r.skipped ? "info" : "success"](r.skipped ? "Clipul era deja importat" : "Clip importat în aplicație (apare în Filme/Evenimente)"),
    onError: (e) => toast.error(apiError(e)),
  });

  const crumbs = useMemo(() => {
    const parts = (effPath || "").split("/").filter(Boolean);
    const acc = [];
    let cur = "";
    for (const p of parts) { cur += "/" + p; acc.push({ name: p, path: cur }); }
    return acc;
  }, [effPath]);

  if (!isAdmin) {
    return <DashboardLayout><div className="max-w-2xl mx-auto mt-12"><Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center"><ShieldAlert className="h-10 w-10 text-muted-foreground" /><p className="text-lg font-medium">Acces restricționat</p><p className="text-sm text-muted-foreground">Doar administratorii pot accesa arhiva NAS.</p></CardContent></Card></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 max-w-5xl" data-testid="nas-archive-page">
        <header>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight flex items-center gap-2"><Archive className="h-6 w-6 text-primary" /> Arhivă NAS</h1>
          <p className="text-sm text-muted-foreground mt-1">Răsfoiește clipurile salvate pe NAS-ul Synology și descarcă-le la cerere. Backup-ul pe NAS păstrează filmele și după ce expiră în Reolink Cloud (30 zile).</p>
        </header>

        {!status?.configured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3">
            <HardDrive className="h-5 w-5 shrink-0 mt-0.5" />
            <div><p className="font-medium">NAS neconfigurat</p><p>Mergi la <strong>Cloud Sync (Reolink)</strong> → secțiunea NAS Synology și completează QuickConnect ID, utilizator, parolă și folderul de bază. Apoi revino aici.</p></div>
          </div>
        )}

        {status?.configured && (
          <>
            {/* Import target */}
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><DownloadCloud className="h-4 w-4" /> Țintă pentru import</CardTitle><CardDescription>Alege șantierul și camera unde vrei să apară clipurile pe care le imporți din NAS în aplicație.</CardDescription></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select value={projectId} onValueChange={(v) => { setProjectId(v); setCameraId(""); }}>
                  <SelectTrigger data-testid="nas-project-select"><SelectValue placeholder="Selectează șantierul" /></SelectTrigger>
                  <SelectContent>{projects?.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={cameraId} onValueChange={setCameraId} disabled={!projectId}>
                  <SelectTrigger data-testid="nas-camera-select"><SelectValue placeholder="Selectează camera" /></SelectTrigger>
                  <SelectContent>{camerasForProject.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Browser */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2"><Folder className="h-4 w-4" /> Explorator NAS</CardTitle>
                  <CardDescription className="flex flex-wrap items-center gap-1 mt-1">
                    <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setPath("")} data-testid="nas-crumb-root"><Home className="h-3 w-3" /> rădăcină</button>
                    {crumbs.map((c) => (
                      <span key={c.path} className="inline-flex items-center gap-1">
                        <ChevronRight className="h-3 w-3" />
                        <button className="hover:text-foreground" onClick={() => setPath(c.path)}>{c.name}</button>
                      </span>
                    ))}
                  </CardDescription>
                </div>
                <Button size="sm" variant="outline" className="bg-background" onClick={() => refetch()} disabled={isFetching} data-testid="nas-refresh-btn">{isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</Button>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {error ? (
                  <p className="text-sm text-destructive py-4">{apiError(error)}</p>
                ) : isFetching && !listing ? (
                  <p className="text-sm text-muted-foreground py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Se încarcă de pe NAS...</p>
                ) : !listing?.items?.length ? (
                  <p className="text-sm text-muted-foreground py-4">Folder gol.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Nume</TableHead><TableHead>Dimensiune</TableHead><TableHead>Modificat</TableHead><TableHead className="text-right">Acțiuni</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {listing.items.map((it) => (
                        <TableRow key={it.path} data-testid={`nas-row-${it.name}`}>
                          <TableCell className="font-medium">
                            {it.isDir ? (
                              <button className="inline-flex items-center gap-2 hover:text-primary" onClick={() => setPath(it.path)} data-testid={`nas-folder-${it.name}`}>
                                <Folder className="h-4 w-4 text-primary" /> {it.name}
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-2"><FileVideo className="h-4 w-4 text-muted-foreground" /> {it.name}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{it.isDir ? "—" : fmtSize(it.size)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{fmtDate(it.mtime)}</TableCell>
                          <TableCell className="text-right">
                            {!it.isDir && (
                              <div className="flex justify-end gap-1.5">
                                <Button size="sm" variant="outline" className="bg-background" onClick={() => downloadFile(it)} disabled={busy === it.path} data-testid={`nas-download-${it.name}`}>
                                  {busy === it.path ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                  <span className="ml-1 hidden sm:inline">Descarcă</span>
                                </Button>
                                <Button size="sm" onClick={() => { if (!cameraId) { toast.error("Alege întâi șantierul și camera de mai sus"); return; } importFile.mutate(it); }} disabled={importFile.isPending} data-testid={`nas-import-${it.name}`}>
                                  <DownloadCloud className="h-3.5 w-3.5" /><span className="ml-1 hidden sm:inline">Importă</span>
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
